import {BrowserRouter, Navigate, Route, Routes} from "react-router";
import {AuthProvider} from "./auth/AuthContext";
import {ThemeProvider} from "./theme/ThemeContext";
import {RequireAuth} from "./auth/RequireAuth";
import {RequireAdmin} from "./auth/RequireAdmin";
import {SiteLayout} from "./layout/SiteLayout";
import {AdminLayout} from "./layout/AdminLayout";
import {HomePage} from "./pages/HomePage";
import {LoginPage} from "./pages/LoginPage";
import {ForgotPasswordPage} from "./pages/ForgotPasswordPage";
import {ResetPasswordPage} from "./pages/ResetPasswordPage";
import {VerifyEmailPage} from "./pages/VerifyEmailPage";
import {NotFoundPage} from "./pages/NotFoundPage";
import {CookiesPage} from "./pages/CookiesPage";
import {PrivacyPage} from "./pages/PrivacyPage";
import {RefundsPage} from "./pages/RefundsPage";
import {TermsPage} from "./pages/TermsPage";
import {AccessibilityPage} from "./pages/AccessibilityPage";
import {AiPolicyPage} from "./pages/AiPolicyPage";
import {ProfilePage} from "./pages/account/ProfilePage";
import {MyLicensesPage} from "./pages/account/MyLicensesPage";
import {CustomersPage, UsersPage} from "./pages/admin/UsersPage";
import {LicensesPage} from "./pages/admin/LicensesPage";
import {LicenseDetailPage} from "./pages/admin/LicenseDetailPage";
import {ProductsPage} from "./pages/admin/ProductsPage";
import {ProductDetailPage} from "./pages/admin/ProductDetailPage";
import {ProductCatalogPage} from "./pages/products/ProductCatalogPage";
import {ProductPage} from "./pages/products/ProductPage";
import {ProductDownloadPage} from "./pages/products/ProductDownloadPage";
import {SupportTicketsPage} from "./pages/support/SupportTicketsPage";
import {NewSupportTicketPage} from "./pages/support/NewSupportTicketPage";
import {ContactPage} from "./pages/support/ContactPage";
import {PressPage} from "./pages/press/PressPage";
import {PressContactPage} from "./pages/press/PressContactPage";
import {SupportTicketPage} from "./pages/support/SupportTicketPage";
import {SupportTicketsAdminPage} from "./pages/admin/SupportTicketsAdminPage";
import {DocumentationIndexPage} from "./pages/docs/DocumentationIndexPage";
import {DocumentationPage} from "./pages/docs/DocumentationPage";
import {GlossaryPage} from "./pages/docs/GlossaryPage";
import {ProductDocumentationEditPage} from "./pages/admin/ProductDocumentationEditPage";
import {DocumentationDownloadsPage} from "./pages/admin/DocumentationDownloadsPage";
import {CheckoutPage} from "./pages/checkout/CheckoutPage";
import {CheckoutCompletePage} from "./pages/checkout/CheckoutCompletePage";
import {CheckoutPayPage} from "./pages/checkout/CheckoutPayPage";
import {MyOrdersPage} from "./pages/account/MyOrdersPage";
import {OrdersAdminPage} from "./pages/admin/OrdersAdminPage";
import {SiteSettingsPage} from "./pages/admin/SiteSettingsPage";
import {AuditLogPage} from "./pages/admin/AuditLogPage";
import {AnalyticsPage} from "./pages/admin/AnalyticsPage";
import {MailingListPage} from "./pages/admin/MailingListPage";
import {NewsConfirmPage} from "./pages/news/NewsConfirmPage";
import {NewsUnsubscribePage} from "./pages/news/NewsUnsubscribePage";
import {ShopProvider} from "./components/ShopContext";
import {SiteStatusProvider} from "./components/SiteStatusContext";
import {PasswordConfirmationProvider} from "./components/PasswordConfirmation";
import {ApiActivityBar} from "./components/ApiActivityBar";

export function App() {
    return (
        <ThemeProvider>
            <ApiActivityBar/>
            <AuthProvider>
                <SiteStatusProvider>
                <ShopProvider>
                <PasswordConfirmationProvider>
                    <BrowserRouter>
                        <Routes>
                            <Route element={<SiteLayout/>}>
                                <Route index element={<HomePage/>}/>
                                <Route path="login" element={<LoginPage/>}/>
                                <Route path="forgot-password" element={<ForgotPasswordPage/>}/>
                                {/* The API's emails link to these two. */}
                                <Route path="reset-password" element={<ResetPasswordPage/>}/>
                                <Route path="verify-email" element={<VerifyEmailPage/>}/>
                                {/* The mailing list's emails link to these: the sign-up's confirmation, and every email's
                                    unsubscribe link (signed in or not). */}
                                <Route path="news/confirm" element={<NewsConfirmPage/>}/>
                                <Route path="news/unsubscribe" element={<NewsUnsubscribePage/>}/>
                                <Route path="terms" element={<TermsPage/>}/>
                                <Route path="privacy" element={<PrivacyPage/>}/>
                                <Route path="refunds" element={<RefundsPage/>}/>
                                <Route path="cookies" element={<CookiesPage/>}/>
                                <Route path="accessibility" element={<AccessibilityPage/>}/>
                                <Route path="ai-policy" element={<AiPolicyPage/>}/>
                                <Route path="products" element={<ProductCatalogPage/>}/>
                                <Route path="products/:sku" element={<ProductPage/>}/>
                                {/* Anyone can read the documentation; its PDFs need a session. */}
                                <Route path="docs">
                                    <Route index element={<DocumentationIndexPage/>}/>
                                    <Route path="glossary" element={<GlossaryPage/>}/>
                                    <Route path=":sku" element={<DocumentationPage/>}/>
                                </Route>

                                {/* The update check's downloadUrl links here (?version=1.2.0). Anyone can download a released
                                    product (it runs as a demo until activated); a beta one's downloads are for its testers. */}
                                <Route path="products/:sku/download" element={<ProductDownloadPage/>}/>
                                {/* Stripe and PayPal send the customer back to /checkout/complete?order= (or, cancelled, to
                                    /checkout/:sku?cancelled=1), and Paddle's checkout opens on /checkout/pay: the API's
                                    OrderDataProvider makes those URLs, with &key= for a guest's order. Signed in or not: a
                                    guest's purchase makes their account. /checkout/pay has its own Content-Security-Policy
                                    (serverless.yml), so it's only ever reached by a full page load. */}
                                <Route path="checkout">
                                    <Route path="complete" element={<CheckoutCompletePage/>}/>
                                    <Route path="pay" element={<CheckoutPayPage/>}/>
                                    <Route path=":sku" element={<CheckoutPage/>}/>
                                </Route>

                                {/* Anyone can open a support ticket (signed out: with an email, followed by the emailed link, which
                                    opens /support/:ticketId?access=); the API's support emails link to /support/:ticketId and
                                    /admin/support/:ticketId. The list of your tickets needs signing in. /contact (Contact us)
                                    opens one that isn't about a license, worded for questions before buying. */}
                                <Route path="contact" element={<ContactPage/>}/>
                                <Route path="press" element={<PressPage/>}/>
                                <Route path="press/contact" element={<PressContactPage/>}/>
                                <Route path="support/new" element={<NewSupportTicketPage/>}/>
                                <Route path="support/:ticketId" element={<SupportTicketPage/>}/>

                                <Route element={<RequireAuth/>}>
                                    <Route path="account">
                                        <Route index element={<Navigate to="profile" replace/>}/>
                                        <Route path="profile" element={<ProfilePage/>}/>
                                        {/* Astrel's license dialog links here (TSA1_LICENSE_PORTAL). */}
                                        <Route path="licenses" element={<MyLicensesPage/>}/>
                                        {/* The API's purchase receipts link here. */}
                                        <Route path="orders" element={<MyOrdersPage/>}/>
                                    </Route>
                                    <Route path="support" element={<SupportTicketsPage/>}/>
                                    <Route path="admin" element={<RequireAdmin/>}>
                                        <Route element={<AdminLayout/>}>
                                            <Route index element={<Navigate to="users" replace/>}/>
                                            <Route path="customers" element={<CustomersPage/>}/>
                                            <Route path="users" element={<UsersPage/>}/>
                                            <Route path="licenses" element={<LicensesPage/>}/>
                                            <Route path="licenses/:licenseId" element={<LicenseDetailPage/>}/>
                                            <Route path="products" element={<ProductsPage/>}/>
                                            <Route path="products/:productId" element={<ProductDetailPage/>}/>
                                            <Route path="products/:productId/documentation" element={<ProductDocumentationEditPage/>}/>
                                            <Route path="support" element={<SupportTicketsAdminPage/>}/>
                                            <Route path="support/:ticketId" element={<SupportTicketPage adminView/>}/>
                                            <Route path="documentation-downloads" element={<DocumentationDownloadsPage/>}/>
                                            <Route path="mailing-list" element={<MailingListPage/>}/>
                                            <Route path="orders" element={<OrdersAdminPage/>}/>
                                            <Route path="analytics" element={<AnalyticsPage/>}/>
                                            <Route path="audit-log" element={<AuditLogPage/>}/>
                                            <Route path="settings" element={<SiteSettingsPage/>}/>
                                            <Route path="shop" element={<Navigate to="/admin/settings" replace/>}/>
                                        </Route>
                                    </Route>
                                </Route>

                                <Route path="*" element={<NotFoundPage/>}/>
                            </Route>
                        </Routes>
                    </BrowserRouter>
                </PasswordConfirmationProvider>
                </ShopProvider>
                </SiteStatusProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}
